import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useRegisterInput from '../Form/useRegisterInput';
import { useFormContext } from '../Form';
import clsx from 'clsx';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MaterialSelect from '@material-ui/core/Select';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import styled from 'styled-components';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import ErrorMessage from '../ErrorMessage';
import Icon from '../Icon';
import isDefined from '@afosto/utils/isDefined';

const HoverOutlinedInput = styled(OutlinedInput)`
  && {
    &.inline {
      &:hover {
        .inlineEditField-adornment {
          visibility: visible !important;
          opacity: 1;
        }
      }

      .MuiSelect-icon {
        opacity: 1;
        transition: ${props =>
          props.theme.transitions.create('opacity', {
            easing: props.theme.transitions.easing.sharp,
            duration: props.theme.transitions.duration.shortest,
          })};
      }

      &.MuiOutlinedInput-notchedOutline {
        transition: ${props =>
          props.theme.transitions.create('border-color', {
            easing: props.theme.transitions.easing.sharp,
            duration: props.theme.transitions.duration.shortest,
          })};
      }

      &.MuiInputBase-root.Mui-disabled {
        color: ${props => props.theme.palette.text.primary};

        .MuiSelect-icon {
          opacity: 0;
        }

        .MuiOutlinedInput-notchedOutline {
          border-color: transparent;
        }
      }
    }
  }
`;

const HiddenIconButton = styled(IconButton)`
  && {
    visibility: hidden;
    opacity: 0;
    margin-top: -6px;
    margin-bottom: -6px;
    transition: ${props =>
      props.theme.transitions.create('opacity', {
        easing: props.theme.transitions.easing.sharp,
        duration: props.theme.transitions.duration.shortest,
      })};
  }
`;

const ClearIconButton = styled(IconButton)`
  && {
    margin-right: 16px;
  }
`;

const StyledChip = styled(Chip)`
  && {
    margin: 2px;
  }
`;

const StyledGrid = styled(Grid)`
  && {
    margin: -8.5px -2px;
  }
`;

const Select = ({
  clearable,
  suggestions: providedSuggestions,
  label,
  id,
  multiple,
  register: shouldRegister,
  renderSelected,
  renderSuggestions,
  getSuggestionLabel,
  margin,
  fullWidth,
  ...props
}) => {
  const formContext = useFormContext();
  const {
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    errors = {},
    setValue,
    setIsEditing,
    watch,
  } = formContext || {};
  const {
    name,
    inline = formInline,
    isEditing = formIsEditing,
    error = !!errors[name],
    errorMessage,
    onEdit,
    onChange,
    readOnly,
    helperText,
    value = (watch && watch(name)) ?? '',
    ...inputProps
  } = props;
  const inputLabelRef = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const isInline = inline && !isEditing;
  const showEditAdornment = isInline && !readOnly;
  const suggestions =
    typeof providedSuggestions === 'function' ? providedSuggestions(name) : providedSuggestions;

  if (isInline) {
    inputProps.disabled = true;
  }

  if (readOnly) {
    inputProps.disabled = true;
  }

  useRegisterInput(name, shouldRegister);

  useEffect(() => {
    setLabelWidth(inputLabelRef.current.offsetWidth);
  }, []);

  /**
   * On edit handler.
   */
  const handleOnEdit = () => {
    if (formContext && !onEdit) {
      setIsEditing(true);
    }

    if (onEdit) {
      onEdit();
    }
  };

  /**
   * Get the suggestion label.
   * @param {string} suggestion
   * @returns {*}
   */
  const renderSuggestionLabel = suggestion => {
    if (typeof getSuggestionLabel === 'function') {
      return getSuggestionLabel(suggestion);
    }

    return suggestion;
  };

  /**
   * Default selected values rendering.
   * @param {any} selected
   * @returns {*}
   */
  const renderSelectedValues = selected => {
    if (Array.isArray(selected)) {
      return (
        <StyledGrid container>
          {selected.map(selectedValue => {
            let selectedLabel = renderSuggestionLabel(selectedValue);
            let selectedDescription = null;

            if (suggestions && suggestions.length > 0) {
              const selectedSuggestion = suggestions.find(
                suggestion => suggestion.value === selectedValue,
              );
              selectedLabel =
                (selectedSuggestion && renderSuggestionLabel(selectedSuggestion.label)) ||
                renderSuggestionLabel(selectedValue);
              selectedDescription =
                (selectedSuggestion && selectedSuggestion.description) || selectedDescription;
            }

            if (selectedDescription) {
              return (
                <Tooltip key={selectedValue} title={selectedDescription} placement="bottom">
                  <StyledChip label={selectedLabel} />
                </Tooltip>
              );
            }

            return <StyledChip key={selectedValue} label={selectedLabel} />;
          })}
        </StyledGrid>
      );
    }

    const selectedSuggestion = suggestions.find(suggestion => suggestion.value === selected);
    return selectedSuggestion ? renderSuggestionLabel(selectedSuggestion.label) : '';
  };

  /**
   * Default suggestions rendering.
   * @returns {*[]}
   */
  const renderDefaultSuggestions = () =>
    Object.values(suggestions).map(
      ({
        label: suggestionLabel,
        value: suggestionValue,
        description: suggestionDescription = null,
      }) => (
        <MenuItem key={suggestionValue} value={suggestionValue}>
          <div>
            {renderSuggestionLabel(suggestionLabel)}
            {suggestionDescription && (
              <Typography variant="caption" color="textSecondary" component="p">
                {suggestionDescription}
              </Typography>
            )}
          </div>
        </MenuItem>
      ),
    );

  /**
   * Handle the change on the select.
   * @param {object|Event} ev
   */
  const handleOnChange = ev => {
    if (formContext && !onChange) {
      setValue(name, ev.target.value);
      return;
    }

    if (onChange) {
      onChange(ev, ev.target.value);
    }
  };

  /**
   * Return the error message.
   */
  const getErrorMessage = () => {
    if (formContext && !errorMessage) {
      return errors[name] && <ErrorMessage errors={errors} name={name} />;
    }

    return errorMessage;
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      variant="outlined"
      margin={margin}
      error={error}
      disabled={inputProps.disabled}
    >
      <InputLabel
        shrink={inline || isDefined(value) || undefined}
        htmlFor={id}
        ref={inputLabelRef}
        disabled={inputProps.disabled}
      >
        {label}
      </InputLabel>
      <MaterialSelect
        {...inputProps}
        onChange={ev => handleOnChange(ev)}
        multiple={multiple}
        value={value}
        input={
          <HoverOutlinedInput
            id={id}
            name={name}
            className={clsx({ inline: isInline || (inline && readOnly) })}
            labelWidth={labelWidth}
            disabled={inputProps.disabled}
            notched={inline || isDefined(value) || undefined}
            endAdornment={
              showEditAdornment ? (
                <HiddenIconButton className="inlineEditField-adornment" onClick={handleOnEdit}>
                  <Icon icon={['fal', 'pencil']} size="small" />
                </HiddenIconButton>
              ) : (
                isDefined(value) &&
                !inputProps.disabled &&
                clearable && (
                  <ClearIconButton
                    className="inlineEditField-adornment"
                    onClick={() => handleOnChange({ target: { value: multiple ? [] : null } })}
                  >
                    <Icon icon={['fal', 'times']} size="small" />
                  </ClearIconButton>
                )
              )
            }
          />
        }
        renderValue={renderSelected || renderSelectedValues}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 48 * 6.5,
            },
          },
        }}
      >
        {renderSuggestions ? renderSuggestions(suggestions, name) : renderDefaultSuggestions()}
      </MaterialSelect>
      {helperText && !error && <FormHelperText>{helperText}</FormHelperText>}
      {error && getErrorMessage() && <FormHelperText>{getErrorMessage()}</FormHelperText>}
    </FormControl>
  );
};

Select.propTypes = {
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  label: PropTypes.string,
  suggestions: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  clearable: PropTypes.bool,
  multiple: PropTypes.bool,
  readOnly: PropTypes.bool,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  margin: PropTypes.string,
  fullWidth: PropTypes.bool,
  renderSelected: PropTypes.func,
  renderSuggestions: PropTypes.func,
  getSuggestionLabel: PropTypes.func,
  register: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
};

Select.defaultProps = {
  id: null,
  label: null,
  suggestions: [],
  inline: undefined,
  isEditing: undefined,
  onEdit: undefined,
  clearable: false,
  multiple: false,
  readOnly: false,
  helperText: undefined,
  error: undefined,
  errorMessage: undefined,
  margin: 'normal',
  fullWidth: true,
  renderSelected: null,
  renderSuggestions: null,
  getSuggestionLabel: undefined,
  register: true,
  onChange: undefined,
  value: undefined,
};

export default Select;
